import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLearners } from '@/services/learners';
import { useAdjustPoints } from '@/services/gamification';
import { useDebounce } from '@/hooks/useDebounce';
import type { Learner } from '@/types/learner';

const schema = z.object({
  learner_id: z.string().min(1, 'Select a learner'),
  is_credit: z.boolean(),
  points: z
    .number({ message: 'Points must be a positive integer' })
    .int('Points must be a whole number')
    .min(1, 'Points must be at least 1'),
  note: z
    .string()
    .min(1, 'A note is required')
    .max(500, 'Note must be 500 characters or less'),
});

type FormData = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

interface AdjustPointsModalProps {
  onClose: () => void;
  onSaved?: () => void;
}

const avatarColors = [
  'bg-violet-500',
  'bg-success-500',
  'bg-pink-500',
  'bg-amber-500',
  'bg-secondary-500',
];
const colorFor = (name: string) =>
  avatarColors[(name.charCodeAt(0) || 0) % avatarColors.length];
const initialsFor = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const AdjustPointsModal: React.FC<AdjustPointsModalProps> = ({ onClose, onSaved }) => {
  const reduceMotion = useReducedMotion();
  const adjust = useAdjustPoints();

  const [search, setSearch] = useState('');
  const [selectedLearner, setSelectedLearner] = useState<Learner | null>(null);
  const [isCredit, setIsCredit] = useState(true);
  const [points, setPoints] = useState<string>('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const debouncedSearch = useDebounce(search, 300);
  const { data: learnersData, isFetching } = useLearners({
    page: 1,
    per_page: 8,
    search: debouncedSearch || undefined,
  });
  const suggestions = useMemo(
    () => learnersData?.learners ?? [],
    [learnersData]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const validate = (): FormData | null => {
    const candidate = {
      learner_id: selectedLearner?.id ?? '',
      is_credit: isCredit,
      points: Number(points),
      note: note.trim(),
    };
    const result = schema.safeParse(candidate);
    if (result.success) {
      setErrors({});
      return result.data;
    }
    const errs: FieldErrors = {};
    result.error.issues.forEach((issue) => {
      const key = issue.path[0] as keyof FormData;
      errs[key] = issue.message;
    });
    setErrors(errs);
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = validate();
    if (!parsed) return;

    try {
      await adjust.mutateAsync({
        learnerId: parsed.learner_id,
        data: {
          points: parsed.points,
          is_credit: parsed.is_credit,
          note: parsed.note,
        },
      });
      toast.success(
        parsed.is_credit
          ? `Gave ${parsed.points} points`
          : `Took ${parsed.points} points back`
      );
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to adjust points. Please try again.';
      toast.error(message);
    }
  };

  const showSuggestions =
    !selectedLearner && (search.length > 0 || suggestions.length > 0);

  const panelInitial = reduceMotion
    ? { opacity: 0 }
    : { opacity: 0, scale: 0.95, y: 8 };
  const panelAnimate = reduceMotion
    ? { opacity: 1 }
    : { opacity: 1, scale: 1, y: 0 };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 bg-[rgba(11,11,11,0.7)]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={panelInitial}
        animate={panelAnimate}
        exit={panelInitial}
        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
        className="relative w-full md:max-w-lg bg-background-primary rounded-t-2xl md:rounded-2xl shadow-xl flex flex-col max-h-[90vh]"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-border-primary flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              Adjust Points
            </h3>
            <p className="body-small text-text-secondary mt-0.5">
              Give points to a learner, or take points back.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-full text-text-secondary hover:text-text-primary hover:bg-background-secondary transition-colors active:scale-[0.97]"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-y-auto"
        >
          <div className="px-6 py-5 space-y-5">
            {/* Learner search */}
            <div>
              <label className="block body-small font-medium text-text-primary mb-1.5">
                Learner
              </label>
              {selectedLearner ? (
                <div className="flex items-center gap-3 px-3 py-2.5 border border-border-primary rounded-lg bg-background-secondary">
                  {selectedLearner.avatar_url ? (
                    <img
                      src={selectedLearner.avatar_url}
                      alt={selectedLearner.full_name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${colorFor(selectedLearner.full_name)}`}
                    >
                      {initialsFor(selectedLearner.full_name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="body-small font-semibold text-text-primary truncate">
                      {selectedLearner.full_name}
                    </p>
                    <p className="text-xs text-text-secondary truncate">
                      {selectedLearner.email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLearner(null);
                      setSearch('');
                    }}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium transition-colors"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm text-text-primary bg-background-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors ${
                      errors.learner_id
                        ? 'border-error-primary'
                        : 'border-[rgba(189,188,201,0.4)]'
                    }`}
                    autoFocus
                  />
                  {showSuggestions && (
                    <ul className="mt-2 max-h-56 overflow-y-auto border border-border-primary rounded-lg divide-y divide-border-primary bg-background-primary">
                      {isFetching && suggestions.length === 0 ? (
                        <li className="px-3 py-3 text-sm text-text-secondary">
                          Searching…
                        </li>
                      ) : suggestions.length === 0 ? (
                        <li className="px-3 py-3 text-sm text-text-secondary">
                          No learners match &ldquo;{search}&rdquo;
                        </li>
                      ) : (
                        suggestions.map((l) => (
                          <li key={l.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedLearner(l);
                                setErrors((prev) => ({
                                  ...prev,
                                  learner_id: undefined,
                                }));
                              }}
                              className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-background-secondary transition-colors"
                            >
                              {l.avatar_url ? (
                                <img
                                  src={l.avatar_url}
                                  alt={l.full_name}
                                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${colorFor(l.full_name)}`}
                                >
                                  {initialsFor(l.full_name)}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="body-small font-medium text-text-primary truncate">
                                  {l.full_name}
                                </p>
                                <p className="text-xs text-text-secondary truncate">
                                  {l.email}
                                </p>
                              </div>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </>
              )}
              {errors.learner_id && (
                <p className="mt-1 text-xs text-error-700">{errors.learner_id}</p>
              )}
            </div>

            {/* Direction */}
            <div>
              <label className="block body-small font-medium text-text-primary mb-1.5">
                Direction
              </label>
              <div className="inline-flex p-1 bg-background-secondary rounded-lg border border-border-primary">
                <button
                  type="button"
                  onClick={() => setIsCredit(true)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors active:scale-[0.97] ${
                    isCredit
                      ? 'bg-background-primary text-success-700 dark:text-success-400 shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  + Give
                </button>
                <button
                  type="button"
                  onClick={() => setIsCredit(false)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors active:scale-[0.97] ${
                    !isCredit
                      ? 'bg-background-primary text-error-700 dark:text-error-400 shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  − Take
                </button>
              </div>
            </div>

            {/* Points */}
            <div>
              <label
                htmlFor="adjust-points"
                className="block body-small font-medium text-text-primary mb-1.5"
              >
                Points
              </label>
              <input
                id="adjust-points"
                type="number"
                min={1}
                step={1}
                value={points}
                onChange={(e) => {
                  setPoints(e.target.value);
                  setErrors((prev) => ({ ...prev, points: undefined }));
                }}
                placeholder="0"
                className={`w-full px-3.5 py-2.5 border rounded-lg text-sm text-text-primary bg-background-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors tabular-nums ${
                  errors.points
                    ? 'border-error-primary'
                    : 'border-[rgba(189,188,201,0.4)]'
                }`}
              />
              {errors.points && (
                <p className="mt-1 text-xs text-error-700">{errors.points}</p>
              )}
            </div>

            {/* Note */}
            <div>
              <label
                htmlFor="adjust-note"
                className="block body-small font-medium text-text-primary mb-1.5"
              >
                Note <span className="text-text-tertiary font-normal">(required)</span>
              </label>
              <textarea
                id="adjust-note"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setErrors((prev) => ({ ...prev, note: undefined }));
                }}
                rows={3}
                maxLength={500}
                placeholder="e.g. Bonus for outstanding course completion"
                className={`w-full px-3.5 py-2.5 border rounded-lg text-sm text-text-primary bg-background-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors resize-none ${
                  errors.note
                    ? 'border-error-primary'
                    : 'border-[rgba(189,188,201,0.4)]'
                }`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.note ? (
                  <p className="text-xs text-error-700">{errors.note}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-text-tertiary tabular-nums">
                  {note.length}/500
                </span>
              </div>
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-border-primary flex items-center justify-end gap-3 flex-shrink-0 bg-background-primary">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost active:scale-[0.97]"
              disabled={adjust.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary inline-flex items-center justify-center min-w-[120px] active:scale-[0.97] disabled:opacity-60"
              disabled={adjust.isPending}
            >
              {adjust.isPending ? 'Saving…' : 'Adjust Points'}
            </button>
          </footer>
        </form>
      </motion.div>
    </div>
  );
};

export default AdjustPointsModal;
